import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import SectionHeader from "./SectionHeader";
import Reveal from "./Reveal";
import {
  Bot,
  GraduationCap,
  Wand2,
  CheckCircle,
  XCircle,
  ArrowRight,
  MessageSquare,
} from "lucide-react";

/**
 * KI-Ankerpunkt (#ki): macht sichtbar, dass Klartech Müritz KI-Unterstützung
 * anbietet – bewusst ausschließlich für kleine Firmen und Selbstständige,
 * nicht für Mittelstand oder Konzerne.
 */
const AISection = () => {
  const offers = [
    {
      icon: Bot,
      title: "KI-Chatbot für Ihre Website",
      desc: "Beantwortet Kundenanfragen rund um die Uhr – freundlich, in Ihrem Ton und in wenigen Minuten eingebunden.",
      link: { to: "/chatbot-produkt", label: "Zum KI-Chatbot" },
    },
    {
      icon: GraduationCap,
      title: "KI-Beratung & Einführung",
      desc: "ChatGPT & Co. verständlich erklärt: Wir zeigen Ihnen, wo KI in Ihrem Betrieb wirklich Zeit spart – und wo nicht.",
    },
    {
      icon: Wand2,
      title: "Kleine Automatisierungen",
      desc: "E-Mails vorformulieren, Texte zusammenfassen, Angebote schneller schreiben – wir richten es für Sie ein.",
    },
  ];

  const fit = [
    { ok: true, label: "Kleine Unternehmen & Teams" },
    { ok: true, label: "Selbstständige & Freiberufler" },
    { ok: false, label: "Mittelständische Unternehmen" },
    { ok: false, label: "Große Firmen & Konzerne" },
  ];

  return (
    <section id="ki" className="relative py-20 md:py-28 overflow-hidden bg-gradient-navy text-white">
      {/* Leiterbahn-Muster aus dem Logo */}
      <div className="absolute inset-0 bg-circuit-light pointer-events-none" aria-hidden="true" />
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-flame/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6">
        {/* Badge über der Überschrift */}
        <Reveal className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-2.5 rounded-full bg-white/10 border border-white/15 px-5 py-2.5 text-sm font-bold text-flame-soft backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-flame-soft" aria-hidden="true" />
            KI-Unterstützung – speziell für kleine Firmen
          </div>
        </Reveal>

        <SectionHeader
          number="03"
          title="Künstliche Intelligenz –"
          highlight="klar & verständlich"
          subtitle="Wir bringen KI dorthin, wo sie wirklich hilft: in kleine Betriebe vor Ort. Ohne Fachchinesisch, ohne Projektmonster – eingerichtet, erklärt und betreut von Ihrem IT-Partner an der Müritz."
          onDark
        />

        {/* Angebots-Karten */}
        <div className="grid gap-6 md:grid-cols-3 mb-14">
          {offers.map((offer, i) => {
            const Icon = offer.icon;
            return (
              <Reveal
                key={offer.title}
                delay={i * 90}
                className="group relative rounded-3xl bg-white/[0.06] border border-white/10 p-6 md:p-8 backdrop-blur-sm transition-all duration-300 hover:bg-white/[0.1] hover:border-accent/40 hover:-translate-y-1.5"
              >
                {/* Leiterbahn-Ecke */}
                <span className="absolute top-5 right-5 h-2.5 w-2.5 rounded-full border-2 border-flame/60 group-hover:border-flame transition-colors" aria-hidden="true" />

                <div className="inline-flex rounded-2xl bg-white/10 border border-white/15 p-4 mb-6 transition-colors group-hover:bg-white/15">
                  <Icon className="h-7 w-7 text-accent" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{offer.title}</h3>
                <p className="text-white/70 leading-relaxed">{offer.desc}</p>

                {offer.link && (
                  <Link
                    to={offer.link.to}
                    className="mt-5 inline-flex items-center gap-2 text-flame-soft font-bold hover:text-white transition-colors"
                  >
                    {offer.link.label}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                )}
              </Reveal>
            );
          })}
        </div>

        {/* Für wen? – klare Abgrenzung */}
        <Reveal className="grid gap-8 lg:grid-cols-[1.2fr_1fr] items-center rounded-[2rem] bg-white/[0.05] border border-white/10 p-6 md:p-12 backdrop-blur-sm">
          <div>
            <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-4">
              Für wen ist unsere KI-Unterstützung?
            </h3>
            <p className="text-white/70 text-lg leading-relaxed mb-6">
              Wir konzentrieren uns bewusst auf <strong className="text-white">kleine Firmen</strong> –
              dort, wo persönliche Betreuung den größten Unterschied macht. Große IT-Projekte für
              Mittelstand und Konzerne überlassen wir anderen.
            </p>

            <div className="grid sm:grid-cols-2 gap-3">
              {fit.map((item) => (
                <div
                  key={item.label}
                  className={`flex items-center gap-3 rounded-xl px-4 py-3 border ${
                    item.ok
                      ? "bg-emerald-400/10 border-emerald-300/25 text-white"
                      : "bg-white/[0.03] border-white/10 text-white/45"
                  }`}
                >
                  {item.ok ? (
                    <CheckCircle className="h-5 w-5 text-emerald-400 shrink-0" />
                  ) : (
                    <XCircle className="h-5 w-5 text-white/35 shrink-0" />
                  )}
                  <span className={`font-semibold ${item.ok ? "" : "line-through decoration-white/30"}`}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA-Panel */}
          <div className="relative rounded-3xl bg-white/[0.07] border border-white/15 p-6 md:p-8 text-center">
            <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-gradient-flame flex items-center justify-center shadow-soft">
              <MessageSquare className="h-8 w-8 text-navy-deep" />
            </div>
            <h4 className="text-xl font-bold text-white mb-2">Neugierig, was KI für Sie tun kann?</h4>
            <p className="text-white/65 mb-6">
              Im kostenlosen Erstgespräch finden wir es gemeinsam heraus – ehrlich und ohne Verkaufsdruck.
            </p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
                className="w-full rounded-full bg-gradient-flame hover:opacity-95 text-navy-deep px-6 py-6 text-base font-extrabold shadow-soft transition-all hover:-translate-y-0.5"
              >
                Kostenloses Erstgespräch
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Link to="/chatbot-produkt" className="w-full">
                <Button
                  variant="outline"
                  className="w-full rounded-full border-2 border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white px-6 py-6 text-base font-bold transition-all"
                >
                  KI-Chatbot ansehen
                </Button>
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default AISection;
